import {
  DuperbrowseStoreSlice,
  DuperbrowseNavigationItem,
  DuperbrowseItemId,
  ActiveDuperbrowseStream,
  RootStream,
  DuperbrowseItem,
  DuperbrowseNormalizedItem,
  DuperbrowseItemSchema,
  isSimpleStream,
  DuperbrowseStream,
  isLoadableStream,
  DuperbrowseLoadableStream,
} from './types';
import { selectors as streamSelectors } from '../../Stream';
import { reduxKey, initialState } from './constants';
import { getStreamItemId, isDeviation } from './helpers';
import { getIsMobile, getPagingMode } from '../../publicSession/selectors';
import { getCurrentModal } from '../../Modals/redux/selectors';
import {
  getExtendedDeviationById,
  getDeviationById,
} from '../../redux/deviations/selectors';
import { createSelector } from 'reselect';

function getDuperbrowseState(state: DuperbrowseStoreSlice) {
  return state[reduxKey] || initialState;
}

function getTopStream(
  state: DuperbrowseStoreSlice
): ActiveDuperbrowseStream | RootStream | undefined {
  const { streamStack, rootStream } = getDuperbrowseState(state);
  if (!streamStack.length) {
    return rootStream;
  }

  return streamStack[0];
}

function getExtendedItem(
  state: DuperbrowseStoreSlice,
  item: DuperbrowseNormalizedItem | undefined,
  itemSchema: DuperbrowseItemSchema | undefined
): DuperbrowseItem | undefined {
  if (item === undefined) {
    return;
  }

  switch (itemSchema) {
    case 'deviation':
      if (isDeviation(item)) {
        return getExtendedDeviationById(state, item.deviationId) ?? item;
      }
      if (typeof item === 'number' || typeof item === 'string') {
        return getExtendedDeviationById(state, item);
      }
      return;
    default:
      return item as DuperbrowseItem;
  }
}

function getSimpleItem(
  state: DuperbrowseStoreSlice,
  item: DuperbrowseNormalizedItem | undefined,
  itemSchema: DuperbrowseItemSchema | undefined
): DuperbrowseItem | undefined {
  if (item === undefined) {
    return;
  }

  switch (itemSchema) {
    case 'deviation':
      if (isDeviation(item)) {
        return item;
      }
      if (typeof item === 'number' || typeof item === 'string') {
        return getDeviationById(state, item);
      }
      return;
    default:
      return item as DuperbrowseItem;
  }
}

export function getStreamItems(state, stream: DuperbrowseStream) {
  if (isSimpleStream(stream)) {
    return stream.items;
  }

  if (stream.streamItemSelector) {
    return stream.streamItemSelector(state, stream.streamId);
  }

  const streamItems = streamSelectors.getItems<DuperbrowseNormalizedItem>(
    state,
    stream.streamId
  );
  return streamItems;
}

export function getCurrentItem(state: DuperbrowseStoreSlice) {
  const topStream = getTopStream(state);
  if (!topStream) {
    return;
  }

  const items = getStreamItems(state, topStream);

  const item = items.find(
    i => getStreamItemId(i) === topStream.currentOpenItem
  );

  return getExtendedItem(state, item, topStream.itemSchema);
}

function getCurrentItemIndex(state: DuperbrowseStoreSlice) {
  const currentStream = getTopStream(state);
  if (!currentStream) {
    return -1;
  }

  const items = getStreamItems(state, currentStream);

  return items.map(getStreamItemId).indexOf(currentStream.currentOpenItem);
}

function getStreamItemAtIndex(state: DuperbrowseStoreSlice, index: number) {
  const currentStream = getTopStream(state);
  if (!currentStream || index < 0) {
    return;
  }

  const items = getStreamItems(state, currentStream);
  return items[index];
}

function getNextItemNormalized(state: DuperbrowseStoreSlice) {
  const currentStream = getTopStream(state);

  if (!currentStream) {
    return;
  }

  const index = getCurrentItemIndex(state);

  if (index < 0) {
    return;
  }

  return getStreamItemAtIndex(state, index + 1);
}

export function getNextItem(state: DuperbrowseStoreSlice) {
  const currentStream = getTopStream(state);
  if (!currentStream) {
    return;
  }

  return getExtendedItem(
    state,
    getNextItemNormalized(state),
    currentStream.itemSchema
  );
}

function getPrevItemNormalized(state: DuperbrowseStoreSlice) {
  const currentStream = getTopStream(state);
  if (!currentStream) {
    return;
  }

  const index = getCurrentItemIndex(state);

  if (index < 0) {
    return;
  }

  return getStreamItemAtIndex(state, index - 1);
}

export function getPrevItem(state: DuperbrowseStoreSlice) {
  const currentStream = getTopStream(state);
  if (!currentStream) {
    return;
  }

  return getExtendedItem(
    state,
    getPrevItemNormalized(state),
    currentStream.itemSchema
  );
}

export function getIsDuperbrowseActive(state: DuperbrowseStoreSlice) {
  return getDuperbrowseState(state).isSessionActive;
}

export function getItemIdsForPreload(
  state: DuperbrowseStoreSlice,
  preloadDirection: 'next' | 'prev' | undefined
) {
  const nextItem = getNextItemNormalized(state);
  const prevItem = getPrevItemNormalized(state);

  const shouldPreloadDirection = (direction: typeof preloadDirection) =>
    !preloadDirection || preloadDirection === direction;

  const navigation = [
    nextItem && shouldPreloadDirection('next') && getStreamItemId(nextItem),
    prevItem && shouldPreloadDirection('prev') && getStreamItemId(prevItem),
  ].filter(item => !!item);

  return navigation as DuperbrowseItemId[];
}

export function getDuperbrowseNavigation(
  state: DuperbrowseStoreSlice
): DuperbrowseNavigationItem {
  return {
    nextItem: getNextItem(state),
    prevItem: getPrevItem(state),
  };
}

export function getDuperbrowseStreamParent(state: DuperbrowseStoreSlice) {
  const {
    streamStack: [currentStream],
  } = getDuperbrowseState(state);
  return currentStream && currentStream.parent;
}

export function getIsKeyboardDuperbrowseAllowed(state) {
  // say no to mobile
  if (getIsMobile(state)) {
    return false;
  }

  // modals get in the way, too
  if (getCurrentModal(state)) {
    return false;
  }

  return true;
}

export function getIsLoading(state) {
  const currentStreamId = getCurrentStreamId(state);
  if (
    currentStreamId &&
    streamSelectors.getIsValidStream(state, currentStreamId)
  ) {
    return streamSelectors.isFetching(state, currentStreamId);
  }
  return false;
}

export function getLastViewedItem(state) {
  return getDuperbrowseState(state).lastViewedItem;
}

export const getCurrentBiMetadata = createSelector(
  [getDuperbrowseState, (state, itemId) => itemId],
  (state, itemId) => {
    return state?.currentBiMetadata?.[itemId] ?? '';
  }
);

/** Tries to return the index in `streams` of the stream with the given `streamId`
 *  that contains an item with the given `itemId. Returns -1 if not found.  */
export function findIndexOfStreamWithItem(
  state,
  streams: DuperbrowseStream[],
  streamId: string,
  itemId: DuperbrowseItemId
): number {
  for (const [index, stream] of streams.entries()) {
    if (!stream || stream.streamId !== streamId) {
      continue;
    }

    const streamItems = getStreamItems(state, stream);
    if (streamItems.some(item => getStreamItemId(item) === itemId)) {
      return index;
    }
  }
  return -1;
}

/** Tries to return the index in the duperbrowse streamStack of the stream with
 *  the given `streamId` that contains an item with the given `itemId.
 *  Returns -1 if not found.  */
export function getStackIndexOfStreamWithItem(
  state,
  streamId: string,
  itemId: DuperbrowseItemId
) {
  const { streamStack } = getDuperbrowseState(state);
  return findIndexOfStreamWithItem(state, streamStack, streamId, itemId);
}

/** Returns true if it can find a stream with the given id and item in the
 *  combined streamStack and rootStream array. */
export function getIsValidStreamItem(
  state,
  streamId: string,
  itemId: DuperbrowseItemId
) {
  const { streamStack, rootStream } = getDuperbrowseState(state);
  const streams = rootStream ? [...streamStack, rootStream] : streamStack;
  return findIndexOfStreamWithItem(state, streams, streamId, itemId) > -1;
}

export function getCurrentStreamId(state) {
  const {
    streamStack: [topStream],
    rootStream,
  } = getDuperbrowseState(state);

  if (topStream) {
    return topStream.streamId;
  }

  if (rootStream) {
    return rootStream.streamId;
  }
}

export function getPreloadingEnabled(state) {
  const {
    streamStack: [topStream],
    rootStream,
  } = getDuperbrowseState(state);

  if (topStream) {
    return topStream.preloadEnabled ?? true;
  }

  if (rootStream) {
    return rootStream.preloadEnabled ?? true;
  }

  return true;
}

export function getRootLocation(state) {
  return getDuperbrowseState(state).rootLocation;
}

export function getCurrentStreamSchema(state: DuperbrowseStoreSlice) {
  const currentStream = getTopStream(state);
  return currentStream && currentStream.itemSchema;
}

export function getIsAnyStreamActive(state: DuperbrowseStoreSlice) {
  const duperState = getDuperbrowseState(state);
  return duperState.streamStack.length > 0 || !!duperState.rootStream;
}

export function getIsHistoryManagementEnabled(state: DuperbrowseStoreSlice) {
  return getDuperbrowseState(state)?.isHistoryManagementEnabled ?? true;
}

export function getNeedsToLoadMore(
  state: DuperbrowseStoreSlice,
  direction: 'next' | 'prev'
) {
  // we never load more in pagination mode
  if (getPagingMode(state) === 'page') {
    return false;
  }

  const currentStream = getTopStream(state);
  if (
    !currentStream ||
    !isLoadableStream(currentStream) ||
    getIsLoading(state)
  ) {
    return false;
  }

  if ((currentStream as DuperbrowseLoadableStream).enableLoader) {
    const items = getStreamItems(state, currentStream);
    const currentIndex = getCurrentItemIndex(state);
    const lookAheadSteps = 2;

    switch (direction) {
      case 'next':
        return (
          streamSelectors.hasMore(state, currentStream.streamId) &&
          currentIndex + lookAheadSteps >= items.length
        );
      case 'prev':
        return (
          streamSelectors.getHasLess(state, currentStream.streamId) &&
          currentIndex - lookAheadSteps < 0
        );
      default:
        return false;
    }
  }

  return false;
}

export function getCurrentDuperbrowseContext(
  state: DuperbrowseStoreSlice,
  itemCount = 9
) {
  const currentStream = getTopStream(state);
  if (!currentStream) {
    return;
  }
  const items = getStreamItems(state, currentStream);
  const currentIndex = getCurrentItemIndex(state);
  if (currentIndex < 0) {
    return;
  }

  const itemsToDuperbrowseItemList = (array: DuperbrowseNormalizedItem[]) =>
    array
      .map(item => getSimpleItem(state, item, currentStream.itemSchema))
      .filter(item => !!item) as DuperbrowseItem[];

  if (items.length <= itemCount) {
    return itemsToDuperbrowseItemList(items);
  }

  // we want half the items before the current one, if possible
  const itemsToShowBefore = Math.floor((itemCount - 1) / 2);
  const startIndex = Math.max(currentIndex - itemsToShowBefore, 0);
  return itemsToDuperbrowseItemList(
    items.slice(startIndex, startIndex + itemCount)
  );
}

export function getLoadCount(state: DuperbrowseStoreSlice) {
  return getDuperbrowseState(state).loadCount ?? 0;
}

export function getRootStream(state) {
  return getDuperbrowseState(state).rootStream;
}
